import type { StudentAssignmentCubit } from '@breakoutlearning/firebase-repository/cubits/StudentAssignmentCubit'
import { DialogCloseButton } from 'components/dialogs/DialogCloseButton'
import { Money } from 'components/icons/Money'
import { useDialogs } from 'hooks/dialogs'
import { useBreakoutUser } from 'hooks/profile'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { Spinner } from 'components/Spinner'
import { useRepository } from 'hooks/auth'
import { useTranslationTyped } from 'i18n/i18n'
import { BreakoutButton } from 'components/design-system/BreakoutButton'

export const PurchaseDialogBody = observer(function PurchaseDialogBody({
  cubit,
  headingId,
}: {
  cubit: StudentAssignmentCubit
  headingId: string
}) {
  const repo = useRepository()
  const { availableTokens } = useBreakoutUser()
  const { usesSectionPass } = cubit.section

  const tokensRequired = cubit.slideDeck.data.slideDeckPrice
  const totalPrice = (
    Math.max((tokensRequired - availableTokens) * 10, 0) / 100
  ).toFixed(2)
  const { popDialog } = useDialogs()
  const { tt } = useTranslationTyped()
  const [waitingForStripeURL, setWaitingForStripeURL] = useState(false)

  useEffect(() => {
    repo.logEvent('purchase_dialog_opened')
  }, [repo])

  if (waitingForStripeURL) {
    return (
      <div className="flex flex-grow items-center justify-center">
        <Spinner />
      </div>
    )
  }

  const sectionStartDate = cubit.section.data.sectionStartDate
  const isSectionNotStarted = sectionStartDate && sectionStartDate > new Date()

  // todo: designs for section pass purchase
  if (usesSectionPass) {
    return (
      <>
        <DialogCloseButton />
        <h1 id={headingId} className="mb-4px text-center text-headline-large">
          {tt.pages.student.assignment.dialogs.purchase_dialog_body.purchase_section_pass()}
        </h1>
        <p className="mb-1 text-center text-body-medium">
          {tt.pages.student.assignment.dialogs.purchase_dialog_body.purchase_section_pass_description()}
        </p>
        <strong className="mb-8px mx-auto table text-display-medium">
          ${cubit.libraryObject.sectionPassPriceDollars}
        </strong>
        <PurchaseButton
          disabled={isSectionNotStarted}
          onPurchase={async () => {
            setWaitingForStripeURL(true)
            try {
              repo.logEvent('purchase_section_pass_button_clicked')
              const { action } = await cubit.createPayment({
                type: 'sectionPass',
                sectionPassSectionId: cubit.section.id,
                beforeRedirect: popDialog,
              })
              if (action === 'purchasedSectionPass') {
                setWaitingForStripeURL(false)
                toast.success(
                  tt.pages.student.assignment.dialogs.purchase_dialog_body.successfully_redeemed_rebates()
                )
              }
            } catch (e) {
              repo.logEvent('purchase_section_pass_button_failed')
              toast.error(tt.student_assignment.pay_with_stripe_failure())
              // rethrow to bubble to global error handler (sentry)
              popDialog()
              throw e
            }
          }}
        />
      </>
    )
  }

  return (
    <>
      <DialogCloseButton />
      <h1 id={headingId} className="mb-4px text-center text-headline-large">
        {tt.student_assignment.purchase_tokens()}
      </h1>
      <p className="mb-1 text-center text-body-medium">
        {tt.student_assignment.purchase_tokens_description()}
      </p>
      <div className="mb-1 w-[8rem] text-body-medium">
        <div className="flex flex-row justify-between">
          <span>{tt.student_assignment.tokens_required_colon()}</span>
          <span className="ml-auto">{tokensRequired}</span>
        </div>
      </div>
      <div className="mb-3 w-[8rem] text-body-medium">
        <div className="flex flex-row justify-between">
          <span>{tt.student_assignment.tokens_available_colon()}</span>
          <span className="text-right">{availableTokens}</span>
        </div>
      </div>
      <div className="bg-lightGray mb-5 flex h-[200px] w-[200px] flex-col items-center justify-center rounded-[20px] p-[20px] shadow-2xl">
        <strong className="mb-8px mx-auto table text-body-medium">
          {tt.student_assignment.tokens_to_purchase()}
        </strong>
        <strong className="mb-8px mx-auto table text-display-medium">
          {Math.max(tokensRequired - availableTokens, 0)}
        </strong>
        <strong className="mb-8px mx-auto table text-headline-medium">
          {tt.student_assignment.tokens()}
        </strong>
        <strong className="mb-8px mx-auto table text-label-medium">
          {tt.student_assignment.total_price_colon()} ${totalPrice}
        </strong>
      </div>
      <PurchaseButton
        disabled={isSectionNotStarted}
        onPurchase={async () => {
          setWaitingForStripeURL(true)
          repo.logEvent('purchase_tokens_button_clicked')
          try {
            const { action } = await cubit.createPayment({
              type: 'token',
              quantityToBuy: tokensRequired - availableTokens,
              beforeRedirect: popDialog,
            })
            if (action === 'purchasedSectionPass') {
              setWaitingForStripeURL(false)
              toast.success(
                tt.pages.student.assignment.dialogs.purchase_dialog_body.successfully_redeemed_rebates()
              )
            }
          } catch (e) {
            repo.logEvent('purchase_tokens_button_failed')
            toast.error(tt.student_assignment.pay_with_stripe_failure())
            // rethrow to bubble to global error handler (sentry)
            popDialog()
            throw e
          }
        }}
      />
    </>
  )
})

const PurchaseButton = function ({
  onPurchase,
  disabled,
}: {
  onPurchase: () => void
  disabled?: boolean
}) {
  const { tt } = useTranslationTyped()
  return (
    <BreakoutButton
      size="large"
      disabled={disabled}
      tooltip={
        disabled
          ? tt.invitation.verify_invitation.section_not_accessible_yet()
          : undefined
      }
      // defaultWidth={false}
      onClick={onPurchase}
    >
      <Money
        size={14}
        className="mr-1 inline-block font-bold text-core-on-primary"
      />

      {tt.student_assignment.pay_with_stripe()}
    </BreakoutButton>
  )
}
